import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { sanitizeHtml } from '@/lib/sanitize-html';
import {
  FileText,
  Download,
  CheckCircle2,
  XCircle,
  Loader2,
  Building2,
  Phone,
  Globe,
  Calendar,
  AlertTriangle,
  PenLine,
  PartyPopper,
  ClipboardList,
  Truck,
  HardHat,
  Mail,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ContractDocumentView, LegalToggle } from '@/components/contracts/ContractDocumentView';
import { ContractDecisionsPanel } from '@/components/contracts/ContractDecisionsPanel';
import {
  DEFAULT_FINANCING_PROGRAMS,
  cashTotalFromSummary,
  decisionBlockMessage,
  decisionsComplete,
  decisionsFromContract,
  findProgram,
  remainingDecisionKeys,
  type ContractDecisions,
  type FinancingProgram,
} from '@/lib/contract-decisions';
import {
  applyOverviewToDocumentProps,
  assumptionsFromContract,
  collectProjectDetailRows,
  discountsFromContract,
  formatJobsiteAddress,
  investmentDetailsFromContract,
  jobsiteFromUnknown,
  normalizePaymentItems,
  overviewFromContractData,
  overviewItemsFromSummary,
  paymentDetailsSource,
  paymentInstructionsFromContract,
  resolveProjectDocumentId,
} from '@/lib/contract-document-props';
import {
  activePublicSignSlot,
  collectClientParties,
  formatSignatureDate,
  isPublicClientSlotSigned,
  nextUnsignedClientIndex,
  OTHER_CLIENT_WAITS_FOR_OWN_LINK,
  readPortalSignedLock,
  remainingClientMustSign,
  signatureForParty,
  signatureForPublicSlot,
  signingNameInputValue,
  signingTermsFromHtml,
  viewSignatureSlots,
  viewSlotsHaveInk,
  writePortalSignedLock,
  type ContractClientParty,
} from '@/lib/contract-signing';
import { ContractSignaturePreview } from '@/components/contracts/ContractSignaturePreview';
import { ContractClientSignSteps } from '@/components/contracts/ContractClientSignSteps';
import { capturedInitialsFromInput, clausesFromSource } from '@/lib/contract-clauses';
import { CHANGE_ORDER_CONSENT_LABEL } from '@/lib/esign-disclosure';
import { buildCsfBlocks, elementScopeBlocks, type CsfBlock } from '@/lib/contract-csf';
import { asArray } from '@/lib/as-array';
import { API_BASE_URL } from '@/lib/api-client';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  areContractDecisionsLocked,
  isClientPortalSignable,
  isWonContractStatus,
} from '@/lib/contract-status';

// ── Types matching the backend public contract response ─────────

interface PublicContract {
  id: string;
  contractId: string;
  status: string;
  contractPdfUrl: string | null;
  createdAt: string;
  clientSignatures: Array<{
    clientName: string;
    signatureUrl: string;
    signedAt: string;
    agreedToTerms: boolean;
    clauseInitials?:
      | Array<{ clauseId?: string; text?: string }>
      | Record<string, { text?: string } | string>
      | null;
  }>;
  companySignature?: string | null;
  companySignedAt: string | null;
  signingSession?: {
    activeSignerId?: string | null;
    activeSlot?: number | null;
    canSign?: boolean;
    clientSigners?: Array<{
      id: string;
      name: string;
      status: string;
      slot: number;
    }>;
  };
  client: {
    firstName: string;
    lastName: string;
    businessName?: string;
    type?: string | null;
    clientType?: string | null;
    municipalityOfProject?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    contactPersons?: Array<{
      firstName?: string;
      lastName?: string;
      position?: string | null;
      email?: string | null;
      contactNumber?: string | null;
    }>;
  };
  company: {
    name: string;
    phone: string;
    website: string;
    mailInCheckPayee?: string | null;
    mailInCheckStreet?: string | null;
    mailInCheckCity?: string | null;
    mailInCheckState?: string | null;
    mailInCheckPostalCode?: string | null;
    paymentLinkUrl?: string | null;
    paymentLinkLabel?: string | null;
    paymentMethods?: string[] | string | null;
  };
  projectLabel: string;
  projectName: string;
  projectNumber?: string | null;
  projectDocumentId?: string | null;
  fieldCode?: string | null;
  projectDescription: string;
  timeline: {
    weekUntilProjectStarts: string;
    weekUntilProjectEnds: string;
    tentativeStartDate?: string;
  };
  overview?: {
    preparedDate?: string | null;
    effectiveDate?: string | null;
    designConsultant?: string | null;
    designConsultantEmail?: string | null;
    licenseNumber?: string | null;
    licenseClassification?: string | null;
    licenseIssuingState?: string | null;
    premisesJurisdiction?: string | null;
    overviewParagraph?: string | null;
    weeksUntilStart?: string | null;
    weeksToComplete?: string | null;
    tentativeStartDate?: string | null;
    allowances?: Array<{
      type?: string | null;
      description?: string | null;
      amount?: number | string | null;
    }>;
  };
  allowances?: Array<{
    type?: string | null;
    description?: string | null;
    amount?: number | string | null;
  }>;
  assumptionsExclusions: string;
  paymentDetails: Array<{
    paymentType: string;
    milestones: string;
    amount: number;
  }>;
  salesRep: {
    name: string;
    email: string;
  };
  contractLanguage: string;
  legalClauses?: Array<{
    id: string;
    title: string;
    html: string;
    requiresInitial: boolean;
  }>;
  limitedLiabilityWarranty: string;
  signatureSectionHtml?: string | null;
  lineItems: Array<{
    name: string;
    category: string;
    cost: number;
  }>;
  estimateTotal: number;
  investmentSummary?: {
    projectDetailsTotal?: number | string;
    discountTotal?: number | string;
    finalTotal?: number | string;
    cashTotal?: number | string;
  } | null;
  decisions?: ContractDecisions | Record<string, unknown>;
  financingPrograms?: FinancingProgram[];
  discounts?: Array<{
    description?: string;
    amount?: number | string;
    isApplied?: boolean;
  }>;
  csfScope?: CsfBlock[];
  projectScope?: {
    statements?: Array<{
      scfCode?: string | null;
      groupLabel?: string | null;
      text: string;
      source?: string | null;
    }>;
    fields?: Array<{ scfCode: string; scfLabel: string; contractLabel?: string | null }>;
  } | null;
  scope?: Array<{
    elementTypeDisplayName?: string | null;
    instanceLabel?: string | null;
    sellPrice?: number | string | null;
    elementTotal?: number | string | null;
    clientIncluded?: boolean;
    statements?: Array<{
      scfCode?: string | null;
      groupLabel?: string | null;
      text: string;
      source?: string | null;
    }>;
    fields?: Array<{ scfCode: string; scfLabel: string; contractLabel?: string | null }>;
  }>;
  // ── Change-order documents ─────────────────────────────────
  contractType?: 'CONTRACT' | 'CHANGE_ORDER';
  changeOrder?: {
    code: string;
    customName: string | null;
    title: string;
    effectiveDate: string | null;
    overviewStatement: string | null;
    scopeSummary: string | null;
    popNewCompletionDate: string | null;
    popDaysAdded: number | null;
    /** investmentSummary-like block for the CO's budget change. */
    budgetChange: ({ finalTotal?: number } & Record<string, unknown>) | null;
  } | null;
  originalContract?: {
    contractId: string;
    executedAt: string | null;
    parties: {
      client: string;
      company: string;
    };
    jobsiteAddress: unknown;
    projectLabel: string;
    totalContractValue: number;
  } | null;
}

// ── Public API calls (no auth required) ─────────────────────────

async function fetchPublicContract(contractId: string): Promise<PublicContract> {
  const response = await fetch(
    `${API_BASE_URL}/contracts/public/${encodeURIComponent(contractId)}`,
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Contract not found');
  }
  return response.json();
}

async function signPublicContract(contractId: string, body: {
  clientName: string;
  signatureBase64: string;
  agreementToTerms: boolean;
  esignConsent: boolean;
  clauseInitials?: Array<{ clauseId: string; text: string }>;
}) {
  const response = await fetch(
    `${API_BASE_URL}/contracts/public/${encodeURIComponent(contractId)}/sign`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to sign contract');
  }
  return response.json();
}

async function updatePublicDecisions(
  token: string,
  body: Record<string, unknown>,
) {
  const response = await fetch(
    `${API_BASE_URL}/contracts/public/${encodeURIComponent(token)}/decisions`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to save decisions');
  }
  return response.json();
}

async function declinePublicContract(contractId: string, body: {
  reason?: string;
  clientName?: string;
}) {
  const response = await fetch(
    `${API_BASE_URL}/contracts/public/${encodeURIComponent(contractId)}/decline`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to decline contract');
  }
  return response.json();
}

// ── Helpers ─────────────────────────────────────────────────────

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'DRAFT':
      return <Badge className="bg-muted text-foreground border-border">Draft</Badge>;
    case 'PENDING_SIGNATURE':
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">Pending Signature</Badge>;
    case 'SENT':
    case 'PRESENTED':
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">Sent</Badge>;
    case 'PARTIALLY_SIGNED':
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">Partially Signed</Badge>;
    case 'CLIENT_SIGNED':
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">Client Signed</Badge>;
    case 'EXECUTED':
    case 'EXECUTED_PAPER':
      return <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">Executed</Badge>;
    case 'WON':
      return <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">Won</Badge>;
    case 'LOST':
      return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700">Lost</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function isSignable(status: string, anotherClientStillNeeded: boolean) {
  return isClientPortalSignable(status, anotherClientStillNeeded);
}

function formatMaybeAddress(address: unknown): string | null {
  const jobsite = jobsiteFromUnknown(address);
  if (jobsite) return formatJobsiteAddress(jobsite);
  if (typeof address === 'string' && address.trim() && !address.trim().startsWith('{')) {
    return address.trim();
  }
  return null;
}

function publicClientName(contract: PublicContract): string {
  const full = `${contract.client.firstName || ''} ${contract.client.lastName || ''}`.trim();
  return full || contract.client.businessName || 'Client';
}

function publicPartiesFrom(contract: PublicContract) {
  return collectClientParties({
    client: {
      fullName: `${contract.client.firstName} ${contract.client.lastName}`.trim(),
      firstName: contract.client.firstName,
      lastName: contract.client.lastName,
      businessName: contract.client.businessName,
      email: contract.client.email,
      phone: contract.client.phone,
      address: contract.client.address,
      city: contract.client.city,
      state: contract.client.state,
      zipCode: contract.client.zipCode,
      contactPersons: contract.client.contactPersons,
    },
    clientSignatures: contract.clientSignatures,
  });
}

function toPublicDocumentProps(contract: PublicContract) {
  const contractValue =
    contract.estimateTotal ||
    asArray<{ amount?: number }>(contract.paymentDetails).reduce(
      (sum, item) => sum + (item.amount || 0),
      0,
    ) ||
    null;
  const payments = normalizePaymentItems(
    paymentDetailsSource(contract),
    contractValue,
  );
  const allCsfBlocks = buildCsfBlocks(contract);
  const csfScope = elementScopeBlocks(allCsfBlocks);
  const projectDetailRows = collectProjectDetailRows({
    csfBlocks: allCsfBlocks,
    projectDetails: (contract as { projectDetails?: unknown }).projectDetails,
    project: contract.originalContract,
  });
  const narrative = contract.projectDescription || '';
  const clientParties = collectClientParties({
    client: {
      fullName: publicClientName(contract),
      firstName: contract.client.firstName,
      lastName: contract.client.lastName,
      businessName: contract.client.businessName,
      clientType: contract.client.clientType || contract.client.type,
      municipalityOfProject: contract.client.municipalityOfProject,
      email: contract.client.email,
      phone: contract.client.phone,
      address: contract.client.address,
      city: contract.client.city,
      state: contract.client.state,
      zipCode: contract.client.zipCode,
      contactPersons: contract.client.contactPersons,
    },
    clientSignatures: contract.clientSignatures,
  });
  const jobsite = jobsiteFromUnknown(contract.originalContract?.jobsiteAddress);
  const base = {
    contractNumber: resolveProjectDocumentId({
      fieldCode: contract.fieldCode,
      projectNumber: contract.projectNumber,
      projectDocumentId: contract.projectDocumentId,
      projectLabel: contract.projectLabel,
      contractId: contract.contractId,
    }),
    title: contract.projectName || contract.projectLabel || 'Construction contract',
    status: contract.status,
    projectName: contract.projectName || contract.projectLabel,
    projectDetailRows,
    projectAddress: formatJobsiteAddress(jobsite) || formatMaybeAddress(contract.originalContract?.jobsiteAddress),
    client: {
      name: publicClientName(contract),
      email: contract.client.email,
      phone: contract.client.phone,
      address: [
        contract.client.address,
        [contract.client.city, contract.client.state, contract.client.zipCode]
          .filter(Boolean)
          .join(', '),
      ]
        .filter(Boolean)
        .join(', '),
      type: contract.client.clientType || contract.client.type,
      businessName: contract.client.businessName,
    },
    company: {
      name: contract.company.name,
      phone: contract.company.phone,
      website: contract.company.website,
    },
    payment: {
      contractValue,
      items: payments,
      ...paymentInstructionsFromContract({
        contractData: {
          display: contract.overview,
          client: contract.client,
          organization: { profile: contract.company },
        },
        company: contract.company,
        client: contract.client,
      }),
    },
    csfScope,
    overviewItems: overviewItemsFromSummary(narrative),
    assumptions:
      assumptionsFromContract(contract) || contract.assumptionsExclusions,
    investmentDetails: investmentDetailsFromContract(contract),
    discounts: discountsFromContract(contract),
    contractLanguage: contract.contractLanguage,
    legalClauses: clausesFromSource({
      html: contract.contractLanguage,
      frozen: contract.legalClauses,
    }),
    clauseInitials: [
      {
        party: 'Client 1',
        initials: capturedInitialsFromInput(
          signatureForParty(
            contract.clientSignatures,
            clientParties[0].name,
            0,
            clientParties[1].name,
          )?.clauseInitials,
        ),
      },
      {
        party: 'Client 2',
        initials: capturedInitialsFromInput(
          signatureForParty(
            contract.clientSignatures,
            clientParties[1].name,
            1,
            clientParties[0].name,
          )?.clauseInitials,
        ),
      },
    ],
    limitedLiabilityWarranty: contract.limitedLiabilityWarranty,
    clientParties,
    contractorSignerName: contract.salesRep?.name || contract.company.name,
    signingTerms: signingTermsFromHtml(
      contract.signatureSectionHtml,
      contract.company.name,
    ),
  };
  return applyOverviewToDocumentProps(
    base,
    overviewFromContractData({
      ...contract,
      createdAt: contract.createdAt,
      companySignedAt: contract.companySignedAt,
      creator: {
        fullName: contract.overview?.designConsultant || contract.salesRep?.name,
        email: contract.overview?.designConsultantEmail || contract.salesRep?.email,
      },
      project: {
        ...(typeof contract.originalContract === 'object' ? contract.originalContract : {}),
        jobsiteAddress: contract.originalContract?.jobsiteAddress,
        municipalityOfProject: contract.client.municipalityOfProject,
      },
      client: {
        ...contract.client,
        municipalityOfProject: contract.client.municipalityOfProject,
      },
    }),
  );
}

// ═════════════════════════════════════════════════════════════════
// ClientContractPage — public client-facing contract view & signing
// ═════════════════════════════════════════════════════════════════

export default function ClientContractPage() {
  const { contractId } = useParams<{ contractId: string }>();

  // ── State ────────────────────────────────────────────────────
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [client1Name, setClient1Name] = useState('');
  const [client2Name, setClient2Name] = useState('');
  const [client1Signature, setClient1Signature] = useState('');
  const [client2Signature, setClient2Signature] = useState('');
  const [client1Accepted, setClient1Accepted] = useState(false);
  const [client2Accepted, setClient2Accepted] = useState(false);
  const [client1Initials, setClient1Initials] = useState<Record<string, string>>({});
  const [client2Initials, setClient2Initials] = useState<Record<string, string>>({});
  const [actionComplete, setActionComplete] = useState<'signed' | 'partial' | 'declined' | null>(
    null,
  );
  const [signOutcome, setSignOutcome] = useState<{
    nextParty?: 'CLIENT' | 'COMPANY';
    status?: string;
  } | null>(null);

  // ── Fetch contract (public, no auth) ─────────────────────────
  const {
    data: contract,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<PublicContract>({
    queryKey: ['public-contract', contractId],
    queryFn: () => fetchPublicContract(contractId!),
    enabled: !!contractId,
    retry: 1,
  });

  const emptyParty: ContractClientParty = {
    name: null,
    email: null,
    phone: null,
    address: null,
  };
  const publicParties: [ContractClientParty, ContractClientParty] = contract
    ? publicPartiesFrom(contract)
    : [emptyParty, emptyParty];
  const defaultClientName = publicParties[0].name || '';
  const nextClientIndex = contract
    ? nextUnsignedClientIndex(publicParties, contract.clientSignatures)
    : 0;
  const anotherClientStillNeeded = contract
    ? remainingClientMustSign(publicParties, contract.clientSignatures)
    : false;
  const decisions = decisionsFromContract(contract ?? {});
  const programs = contract?.financingPrograms?.length
    ? contract.financingPrograms
    : DEFAULT_FINANCING_PROGRAMS;
  const selectedProgram = findProgram(programs, decisions.financingProgramId);
  const remainingChoices = remainingDecisionKeys(decisions, selectedProgram);
  const choicesComplete = decisionsComplete(decisions, selectedProgram);
  const client1Sig = contract
    ? signatureForParty(
        contract.clientSignatures,
        client1Name || publicParties[0].name,
        0,
        client2Name || publicParties[1].name,
      )
    : undefined;
  const client2Sig = contract
    ? signatureForParty(
        contract.clientSignatures,
        client2Name || publicParties[1].name,
        1,
        client1Name || publicParties[0].name,
      )
    : undefined;

  useEffect(() => {
    if (!contract) return;
    const [first, second] = publicPartiesFrom(contract);
    if (!client1Name && first.name) setClient1Name(first.name);
    if (!client2Name && second.name) setClient2Name(second.name);
  }, [client1Name, client2Name, contract]);

  // ── Sign mutation ────────────────────────────────────────────
  const legalClauses = clausesFromSource({
    html: contract?.contractLanguage,
    frozen: contract?.legalClauses,
  });

  const signOneClient = async (input: {
    clientName: string;
    signatureBase64: string;
    accepted: boolean;
    initials: Record<string, string>;
  }) => {
    if (!input.accepted) {
      throw new Error('This client must agree to the terms and electronic signature before signing.');
    }
    return signPublicContract(contractId!, {
      clientName: input.clientName,
      signatureBase64: input.signatureBase64,
      agreementToTerms: true,
      esignConsent: true,
      clauseInitials: Object.entries(input.initials)
        .filter(([, text]) => text.trim())
        .map(([clauseId, text]) => ({ clauseId, text: text.trim() })),
    });
  };

  const signMutation = useMutation({
    mutationFn: async (party: 'client-1' | 'client-2') => {
      if (!choicesComplete) {
        throw new Error(decisionBlockMessage(remainingChoices));
      }
      if (party === 'client-1') {
        const name = signingNameInputValue(client1Name || defaultClientName);
        if (!name || !client1Signature) {
          throw new Error('State Client 1’s name and draw or type a signature.');
        }
        return signOneClient({
          clientName: name,
          signatureBase64: client1Signature,
          accepted: client1Accepted,
          initials: client1Initials,
        });
      }
      const name = signingNameInputValue(client2Name || publicParties[1].name);
      if (!name || !client2Signature) {
        throw new Error('State Client 2’s name and draw or type a signature.');
      }
      return signOneClient({
        clientName: name,
        signatureBase64: client2Signature,
        accepted: client2Accepted,
        initials: client2Initials,
      });
    },
    onSuccess: async (data) => {
      writePortalSignedLock(contractId);
      setSignOutcome({
        nextParty: data?.nextParty,
        status: data?.status,
      });
      const result = await refetch();
      const next = result.data
        ? nextUnsignedClientIndex(
            publicPartiesFrom(result.data),
            result.data.clientSignatures,
          )
        : null;
      setActionComplete(
        data?.nextParty === 'CLIENT' || next !== null ? 'partial' : 'signed',
      );
    },
  });

  // ── Decline mutation ─────────────────────────────────────────
  const declineMutation = useMutation({
    mutationFn: () =>
      declinePublicContract(contractId!, {
        reason: declineReason || undefined,
        clientName: signingNameInputValue(client1Name || defaultClientName),
      }),
    onSuccess: () => {
      setDeclineDialogOpen(false);
      setDeclineReason('');
      setActionComplete('declined');
      refetch();
    },
  });

  const decisionsMutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      updatePublicDecisions(contractId!, patch),
    onSuccess: () => {
      void refetch();
    },
  });

  // ── Loading state ────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading contract...</p>
        </div>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────
  if (isError || !contract) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground mb-2">Contract Not Found</h2>
            <p className="text-sm text-muted-foreground">
              {(error as Error)?.message ||
                'This contract may not exist or the link may be invalid.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Derived values ───────────────────────────────────────────
  const cashTotal = cashTotalFromSummary(
    contract.investmentSummary,
    contract.estimateTotal || 0,
  );
  const canSign = isSignable(contract.status, anotherClientStillNeeded);
  const alreadySignedHere = readPortalSignedLock(contractId);
  const decisionsLocked = areContractDecisionsLocked(
    contract.status,
    alreadySignedHere || !!contract.clientSignatures?.length,
  );
  const sessionComplete =
    actionComplete === 'signed' ||
    actionComplete === 'partial' ||
    alreadySignedHere;
  const activeSlot = activePublicSignSlot({
    parties: publicParties,
    signatures: contract.clientSignatures,
    session: contract.signingSession,
    sessionComplete,
    status: contract.status,
  });
  const client1Signed = isPublicClientSlotSigned(
    1,
    publicParties,
    contract.clientSignatures,
    contract.signingSession,
    contract.status,
  );
  const client2Signed = isPublicClientSlotSigned(
    2,
    publicParties,
    contract.clientSignatures,
    contract.signingSession,
    contract.status,
  );
  const canDecline =
    !['WON', 'LOST'].includes(contract.status) &&
    !client1Signed &&
    activeSlot === 1;
  const isChangeOrder = contract.contractType === 'CHANGE_ORDER';
  const storedSignatureSlots = viewSignatureSlots(contract, {
    contractorName: contract.salesRep?.name || contract.company?.name || null,
  });
  const showStoredInk = viewSlotsHaveInk(storedSignatureSlots);
  const showSignatureStrip = showStoredInk || isWonContractStatus(contract.status);
  const changeOrder = contract.changeOrder;
  const originalContract = contract.originalContract;
  const budgetChangeTotal =
    changeOrder?.budgetChange?.finalTotal ?? contract.estimateTotal;

  return (
    <div className="min-h-screen bg-muted">
      {/* ── Header bar ─────────────────────────────────────────── */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <FileText className="h-5 w-5 text-foreground" />
            </div>
            <div>
              <h1 className="font-newsreader text-xl font-semibold tracking-tight text-foreground">
                {isChangeOrder ? 'Change Order' : 'Contract'}
              </h1>
              <p className="text-xs text-muted-foreground">
                {isChangeOrder && changeOrder
                  ? `${changeOrder.code}${changeOrder.customName ? ` · ${changeOrder.customName}` : ''} — ${contract.projectLabel}`
                  : `${contract.contractId} — ${contract.projectLabel}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(contract.status)}
            {contract.contractPdfUrl && (
              <a
                href={contract.contractPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-foreground bg-muted border border-border rounded-md hover:bg-muted/80 transition-colors"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* ── Action complete banner ──────────────────────────── */}
        {(actionComplete === 'partial' ||
          (nextClientIndex === 1 && !!client1Sig)) && (
          <Card className="border-2 border-amber-200 bg-amber-50/70 dark:border-amber-800 dark:bg-amber-950/20">
            <CardContent className="pt-6 pb-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Your signature is on the contract</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    It stays locked below. The other client must open this agreement and sign separately — you cannot sign for them.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {actionComplete === 'signed' && (
          <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50/50 overflow-hidden dark:border-green-700 dark:from-green-950/30 dark:to-emerald-950/20">
            <CardContent className="pt-6 pb-6">
              {/* Hero section */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4 dark:bg-green-900/30">
                  <PartyPopper className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="font-newsreader text-2xl font-semibold tracking-tight tracking-tight text-foreground mb-1">
                  {isChangeOrder
                    ? 'Change Order Executed — Thank You!'
                    : signOutcome?.nextParty === 'CLIENT' ||
                        contract.status === 'PARTIALLY_SIGNED'
                      ? 'Signature received — Thank You!'
                      : signOutcome?.nextParty === 'COMPANY' ||
                          contract.status === 'CLIENT_SIGNED'
                        ? 'All clients signed — Thank You!'
                        : 'Project Active — Thank You!'}
                </h2>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {isChangeOrder
                    ? 'Your change order has been signed. The updated scope and budget are now part of your project.'
                    : signOutcome?.nextParty === 'CLIENT' ||
                        contract.status === 'PARTIALLY_SIGNED'
                      ? 'The other client will receive an email with a link to add their signature. The company signs last.'
                      : signOutcome?.nextParty === 'COMPANY' ||
                          contract.status === 'CLIENT_SIGNED'
                        ? 'Your sales representative has been emailed to add the company signature and complete the contract.'
                        : "Your contract has been signed and your project is now being activated. Here's what happens next:"}
                </p>
              </div>

              {/* Next steps grid — only after the sale is actually executed */}
              {!isChangeOrder &&
                contract.status !== 'PARTIALLY_SIGNED' &&
                contract.status !== 'CLIENT_SIGNED' &&
                signOutcome?.nextParty !== 'CLIENT' &&
                signOutcome?.nextParty !== 'COMPANY' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto mb-6">
                <div className="flex items-start gap-3 p-3 bg-card/70 rounded-xl border border-green-100">
                  <div className="p-1.5 bg-muted rounded-md flex-shrink-0">
                    <ClipboardList className="h-4 w-4 text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Site Visit Scheduled</p>
                    <p className="text-xs text-muted-foreground">A pre-construction site visit will be coordinated with you shortly.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-card/70 rounded-xl border border-green-100">
                  <div className="p-1.5 bg-muted rounded-md flex-shrink-0">
                    <Truck className="h-4 w-4 text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Materials Ordered</p>
                    <p className="text-xs text-muted-foreground">Your project materials are being ordered and procurement has begun.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-card/70 rounded-xl border border-green-100">
                  <div className="p-1.5 bg-muted rounded-md flex-shrink-0">
                    <HardHat className="h-4 w-4 text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Permits & Kickoff</p>
                    <p className="text-xs text-muted-foreground">Permit applications are underway and a project kickoff meeting will be scheduled.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-card/70 rounded-xl border border-green-100">
                  <div className="p-1.5 bg-muted rounded-md flex-shrink-0">
                    <Mail className="h-4 w-4 text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Onboarding Email Sent</p>
                    <p className="text-xs text-muted-foreground">Check your inbox for a welcome email with your point of contact details.</p>
                  </div>
                </div>
              </div>
              )}

              {/* Contact info */}
              {contract.salesRep.name && (
                <div className="text-center p-3 bg-card/50 rounded-xl border border-green-100 max-w-md mx-auto">
                  <p className="text-xs text-muted-foreground mb-1">Your Point of Contact</p>
                  <p className="text-sm font-bold tracking-tight text-foreground">{contract.salesRep.name}</p>
                  {contract.salesRep.email && (
                    <p className="text-xs text-muted-foreground">{contract.salesRep.email}</p>
                  )}
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Reach out with any questions about your project.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {actionComplete === 'declined' && (
          <div className="rounded-xl border px-4 py-3 flex items-center gap-3 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800">
            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-300">Contract Declined</p>
              <p className="text-xs text-red-600">
                Your contractor has been notified of your decision.
              </p>
            </div>
          </div>
        )}

        {showSignatureStrip && (
          <div className="cd-paper p-4" id="contract-signatures" data-testid="contract-view-signatures">
            <p className="cd-sub-h">Signatures</p>
            <ContractSignaturePreview slots={storedSignatureSlots} />
          </div>
        )}

        {!isChangeOrder && (
          <ContractDocumentView
            {...toPublicDocumentProps(contract)}
            activeInitialsCapture={
              canSign &&
              choicesComplete &&
              !sessionComplete &&
              activeSlot === 1 &&
              !client1Signed
                ? {
                    partyKey: 'client-1',
                    partyLabel: client1Name || defaultClientName || 'Client 1',
                    initials: client1Initials,
                    onInitialChange: (id, text) =>
                      setClient1Initials((prev) => ({ ...prev, [id]: text })),
                  }
                : canSign &&
                    choicesComplete &&
                    !sessionComplete &&
                    activeSlot === 2 &&
                    !client2Signed
                  ? {
                      partyKey: 'client-2',
                      partyLabel: client2Name || 'Client 2',
                      initials: client2Initials,
                      onInitialChange: (id, text) =>
                        setClient2Initials((prev) => ({ ...prev, [id]: text })),
                    }
                  : null
            }
            investmentExtras={
              <ContractDecisionsPanel
                section="choice"
                cashTotal={cashTotal}
                programs={programs}
                decisions={decisions}
                disabled={decisionsLocked}
                pending={decisionsMutation.isPending}
                onSave={async (patch) => {
                  if (decisionsLocked) return;
                  await decisionsMutation.mutateAsync(patch);
                }}
              />
            }
            investmentAfterCalc={
              !decisionsLocked ? (
                <ContractDecisionsPanel
                  section="downpay"
                  cashTotal={cashTotal}
                  programs={programs}
                  decisions={decisions}
                  pending={decisionsMutation.isPending}
                  onSave={async (patch) => {
                    if (decisionsLocked) return;
                    await decisionsMutation.mutateAsync(patch);
                  }}
                />
              ) : null
            }
            hideMilestones={!decisions.financingProgramId}
            signBlockedNote={
              canSign && !choicesComplete ? (
                <p className="cd-sign-blocked" data-testid="contract-sign-blocked">
                  {decisionBlockMessage(remainingChoices)} Signature unlocks after financing, any
                  required application, and down payment are on this contract.
                </p>
              ) : null
            }
            signatureSlots={[
              {
                key: 'client-1',
                role: 'Client 1',
                name: client1Name || publicParties[0].name,
                signedAt: formatSignatureDate(
                  signatureForPublicSlot(
                    1,
                    publicParties,
                    contract.clientSignatures,
                    contract.signingSession,
                  )?.signedAt || client1Sig?.signedAt,
                ),
                signatureImage:
                  signatureForPublicSlot(
                    1,
                    publicParties,
                    contract.clientSignatures,
                    contract.signingSession,
                  )?.signatureUrl ||
                  client1Sig?.signatureUrl ||
                  storedSignatureSlots.find((slot) => slot.key === 'client-1')?.signatureImage ||
                  (client1Sig ? client1Signature : null),
                waitNote:
                  client1Signed && !client1Sig?.signatureUrl
                    ? 'Signed'
                    : !client1Signed && activeSlot !== 1
                      ? OTHER_CLIENT_WAITS_FOR_OWN_LINK
                      : null,
                capture:
                  canSign &&
                  choicesComplete &&
                  !sessionComplete &&
                  activeSlot === 1 &&
                  !client1Signed ? (
                    <ContractClientSignSteps
                      partyKey="client-1"
                      partyName={client1Name || defaultClientName || 'Client 1'}
                      printedName={signingNameInputValue(client1Name || defaultClientName)}
                      onPrintedNameChange={setClient1Name}
                      printedNameLabel="Client 1 signing name"
                      printedNamePlaceholder="Client 1 full name"
                      signature={client1Signature}
                      onSignatureChange={setClient1Signature}
                      clauses={legalClauses}
                      initials={client1Initials}
                      onInitialChange={(id, text) =>
                        setClient1Initials((prev) => ({ ...prev, [id]: text }))
                      }
                      accepted={client1Accepted}
                      onAccepted={setClient1Accepted}
                      pending={signMutation.isPending}
                      onSubmit={() => signMutation.mutate('client-1')}
                    />
                  ) : undefined,
              },
              {
                key: 'client-2',
                role: 'Client 2',
                name: client2Name || publicParties[1].name,
                signedAt: formatSignatureDate(client2Sig?.signedAt),
                signatureImage:
                  client2Sig?.signatureUrl ||
                  storedSignatureSlots.find((slot) => slot.key === 'client-2')?.signatureImage ||
                  (client2Sig ? client2Signature : null),
                waitNote:
                  client2Signed && !client2Sig?.signatureUrl
                    ? 'Signed'
                    : !client2Signed && activeSlot !== 2
                      ? OTHER_CLIENT_WAITS_FOR_OWN_LINK
                      : null,
                capture:
                  canSign &&
                  choicesComplete &&
                  !sessionComplete &&
                  activeSlot === 2 &&
                  !client2Signed ? (
                    <ContractClientSignSteps
                      partyKey="client-2"
                      partyName={client2Name || 'Client 2'}
                      printedName={signingNameInputValue(client2Name)}
                      onPrintedNameChange={setClient2Name}
                      printedNameLabel="Client 2 signing name"
                      printedNamePlaceholder="Client 2 full name"
                      signature={client2Signature}
                      onSignatureChange={setClient2Signature}
                      clauses={legalClauses}
                      initials={client2Initials}
                      onInitialChange={(id, text) =>
                        setClient2Initials((prev) => ({ ...prev, [id]: text }))
                      }
                      accepted={client2Accepted}
                      onAccepted={setClient2Accepted}
                      pending={signMutation.isPending}
                      onSubmit={() => signMutation.mutate('client-2')}
                    />
                  ) : undefined,
              },
              ...storedSignatureSlots
                .filter((slot) => slot.key.startsWith('client-extra-'))
                .map((slot) => ({
                  key: slot.key,
                  role: slot.role,
                  name: slot.name,
                  signedAt: slot.signedAt,
                  signatureImage: slot.signatureImage,
                })),
              {
                key: 'contractor',
                role: 'Contractor',
                name: contract.salesRep?.name || contract.company.name,
                signedAt: formatSignatureDate(contract.companySignedAt),
                signatureImage: contract.companySignature || null,
                waitNote: contract.companySignature
                  ? null
                  : isWonContractStatus(contract.status)
                    ? 'Company has not countersigned yet.'
                    : 'Contractor countersigns after the clients.',
              },
            ]}
          >
            {canSign && actionComplete !== 'signed' && (
              <div data-testid="client-sign-section" className="cd-sign-confirm">
                <p className="cd-sig-note">
                  Each client signs separately and in order. Client 1 finishes first; Client 2 then consents and signs on their own. A finished signature stays locked on this agreement.
                  {!choicesComplete
                    ? ' Signature stays locked until financing and down payment are complete.'
                    : ''}
                </p>
                {canDecline && nextClientIndex === 0 ? (
                  <Button
                    variant="outline"
                    onClick={() => setDeclineDialogOpen(true)}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <XCircle className="h-5 w-5 mr-2" />
                    Decline Contract
                  </Button>
                ) : null}
              </div>
            )}
            {signMutation.isError && (
              <p className="text-sm text-red-600">
                {(signMutation.error as Error)?.message || 'Something went wrong. Please try again.'}
              </p>
            )}
          </ContractDocumentView>
        )}

        {/* ── Company & Client info (change orders keep the original cards) ── */}
        {isChangeOrder && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Contractor
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1">
              <p className="font-bold tracking-tight text-foreground">{contract.company.name}</p>
              {contract.company.phone && (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  {contract.company.phone}
                </p>
              )}
              {contract.company.website && (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  {contract.company.website}
                </p>
              )}
              {contract.salesRep.name && (
                <p className="text-sm text-muted-foreground mt-2">
                  Sales Rep: {contract.salesRep.name}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Client
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1">
              <p className="font-bold tracking-tight text-foreground">
                {contract.client.firstName} {contract.client.lastName}
              </p>
              {contract.client.businessName && (
                <p className="text-sm text-muted-foreground">{contract.client.businessName}</p>
              )}
              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Created{' '}
                {new Date(contract.createdAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </CardContent>
          </Card>
        </div>
        )}

        {/* ── Change order: original contract + CO details ───── */}
        {isChangeOrder && (
          <>
            {/* Tile 1 — Original Contract Reference */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Original Contract Reference</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Contract number</p>
                    <p className="font-semibold text-foreground">
                      {originalContract?.contractId || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Execution date</p>
                    <p className="font-semibold text-foreground">
                      {originalContract?.executedAt
                        ? new Date(originalContract.executedAt).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Parties</p>
                    <p className="font-semibold text-foreground">
                      {originalContract?.parties
                        ? `${originalContract.parties.client} & ${originalContract.parties.company}`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Original contract value</p>
                    <p className="font-semibold text-foreground">
                      {originalContract
                        ? formatCurrency(originalContract.totalContractValue)
                        : '—'}
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Jobsite address</p>
                    <p className="font-semibold text-foreground">
                      {formatMaybeAddress(originalContract?.jobsiteAddress) || '—'}
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Project</p>
                    <p className="font-semibold text-foreground">
                      {originalContract?.projectLabel || contract.projectLabel}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tile 2 — Change Order Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Change Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Change order</p>
                    <p className="font-semibold text-foreground">
                      {changeOrder?.code}
                      {changeOrder?.customName ? ` — ${changeOrder.customName}` : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">{changeOrder?.title}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Effective date</p>
                    <p className="font-semibold text-foreground">
                      {changeOrder?.effectiveDate
                        ? new Date(changeOrder.effectiveDate).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </p>
                  </div>
                  {(changeOrder?.popNewCompletionDate || changeOrder?.popDaysAdded != null) && (
                    <div className="sm:col-span-2">
                      <p className="text-xs font-medium text-muted-foreground mb-0.5">
                        Period of performance change
                      </p>
                      <p className="font-semibold text-foreground">
                        {changeOrder?.popNewCompletionDate
                          ? `New completion date: ${new Date(changeOrder.popNewCompletionDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                          : `${changeOrder?.popDaysAdded} day${changeOrder?.popDaysAdded === 1 ? '' : 's'} added to the schedule`}
                      </p>
                    </div>
                  )}
                </div>

                {changeOrder?.overviewStatement && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Overview</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {changeOrder.overviewStatement}
                    </p>
                  </div>
                )}

                {changeOrder?.scopeSummary && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Scope of change</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {changeOrder.scopeSummary}
                    </p>
                  </div>
                )}

                {asArray(contract.lineItems).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Scope items</p>
                    <div className="space-y-1.5">
                      {asArray<{ name?: string; category?: string; cost?: number }>(contract.lineItems).map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between px-4 py-2.5 bg-muted rounded-lg text-sm"
                        >
                          <div>
                            <p className="font-medium text-foreground">{item.name}</p>
                            {item.category && (
                              <p className="text-xs text-muted-foreground">{item.category}</p>
                            )}
                          </div>
                          <p className="font-semibold text-foreground">
                            {formatCurrency(item.cost ?? 0)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <p className="text-sm font-medium text-foreground">Budget change</p>
                  <p className="font-newsreader text-xl font-semibold tracking-tight text-foreground">
                    {formatCurrency(budgetChangeTotal)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {isChangeOrder && (
          <div className="space-y-3">
            {contract.assumptionsExclusions && (
              <LegalToggle title="Assumptions & exclusions">
                <div
                  className="prose prose-sm max-w-none text-foreground [&_a]:text-blue-700 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(contract.assumptionsExclusions) }}
                />
              </LegalToggle>
            )}
            <LegalToggle title="Contract language" testId="toggle-contract-language">
              {contract.contractLanguage ? (
                <div
                  className="prose prose-sm max-w-none text-foreground [&_a]:text-blue-700 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(contract.contractLanguage) }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">Not provided on this contract.</p>
              )}
            </LegalToggle>
            <LegalToggle title="Limited liability warranty" testId="toggle-limited-liability">
              {contract.limitedLiabilityWarranty ? (
                <div
                  className="prose prose-sm max-w-none text-foreground [&_a]:text-blue-700 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(contract.limitedLiabilityWarranty) }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">Not provided on this contract.</p>
              )}
            </LegalToggle>
          </div>
        )}

        {isChangeOrder && canSign && actionComplete !== 'signed' && (
          <div className="cd-paper space-y-3" data-testid="change-order-sign">
            <div className="cd-sig-grid">
              <div className="cd-sig-panel">
                <div className="cd-sig-panel-h">
                  <span>Client 1</span>
                </div>
                <div className="cd-sig-panel-b">
                  {client1Sig?.signatureUrl || (client1Sig && client1Signature) ? (
                    <img
                      src={client1Sig.signatureUrl || client1Signature}
                      alt="Client 1 signature"
                      className="cd-sig-image"
                    />
                  ) : !sessionComplete && activeSlot === 1 ? (
                    <ContractClientSignSteps
                      partyKey="co-client-1"
                      partyName={client1Name || defaultClientName || 'Client 1'}
                      printedName={signingNameInputValue(client1Name || defaultClientName)}
                      onPrintedNameChange={setClient1Name}
                      printedNameLabel="Client 1 signing name"
                      printedNamePlaceholder="Client 1 full name"
                      signature={client1Signature}
                      onSignatureChange={setClient1Signature}
                      clauses={legalClauses}
                      initials={client1Initials}
                      onInitialChange={(id, text) =>
                        setClient1Initials((prev) => ({ ...prev, [id]: text }))
                      }
                      accepted={client1Accepted}
                      onAccepted={setClient1Accepted}
                      consentLabel={CHANGE_ORDER_CONSENT_LABEL}
                      pending={signMutation.isPending}
                      onSubmit={() => signMutation.mutate('client-1')}
                    />
                  ) : (
                    <p className="cd-sig-wait">Signed. Locked on this change order.</p>
                  )}
                  {client1Sig ? (
                    <p className="cd-muted">
                      Signed{formatSignatureDate(client1Sig.signedAt) ? ` ${formatSignatureDate(client1Sig.signedAt)}` : ''}
                    </p>
                  ) : null}
                </div>
              </div>
              {publicParties[1].name ? (
              <div className="cd-sig-panel">
                <div className="cd-sig-panel-h">
                  <span>Client 2</span>
                </div>
                <div className="cd-sig-panel-b">
                  {client2Sig?.signatureUrl || (client2Sig && client2Signature) ? (
                    <img
                      src={client2Sig.signatureUrl || client2Signature}
                      alt="Client 2 signature"
                      className="cd-sig-image"
                    />
                  ) : !sessionComplete && activeSlot === 2 ? (
                    <ContractClientSignSteps
                      partyKey="co-client-2"
                      partyName={client2Name || 'Client 2'}
                      printedName={signingNameInputValue(client2Name)}
                      onPrintedNameChange={setClient2Name}
                      printedNameLabel="Client 2 signing name"
                      printedNamePlaceholder="Client 2 full name"
                      signature={client2Signature}
                      onSignatureChange={setClient2Signature}
                      clauses={legalClauses}
                      initials={client2Initials}
                      onInitialChange={(id, text) =>
                        setClient2Initials((prev) => ({ ...prev, [id]: text }))
                      }
                      accepted={client2Accepted}
                      onAccepted={setClient2Accepted}
                      consentLabel={CHANGE_ORDER_CONSENT_LABEL}
                      pending={signMutation.isPending}
                      onSubmit={() => signMutation.mutate('client-2')}
                    />
                  ) : (
                    <p className="cd-sig-wait">{OTHER_CLIENT_WAITS_FOR_OWN_LINK}</p>
                  )}
                  {client2Sig ? (
                    <p className="cd-muted">
                      Signed{formatSignatureDate(client2Sig.signedAt) ? ` ${formatSignatureDate(client2Sig.signedAt)}` : ''}
                    </p>
                  ) : null}
                </div>
              </div>
              ) : null}
            </div>
            <div data-testid="client-sign-section" className="cd-sign-confirm">
              <p className="cd-sig-note">
                Each client signs separately and in order. A finished signature stays locked.
              </p>
              {canDecline && nextClientIndex === 0 ? (
                <Button
                  variant="outline"
                  onClick={() => setDeclineDialogOpen(true)}
                  className="border-red-200 text-red-700 hover:bg-red-50"
                >
                  <XCircle className="h-5 w-5 mr-2" />
                  Decline
                </Button>
              ) : null}
            </div>
          </div>
        )}

        {/* ── Signature status ────────────────────────────────── */}
        {asArray(contract.clientSignatures).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <PenLine className="h-4 w-4 text-green-600" />
                Signatures
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {asArray<{ clientName?: string; signedAt?: string }>(contract.clientSignatures).map((sig, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between px-4 py-3 bg-green-50 border border-green-100 rounded-lg dark:bg-green-950/30 dark:border-green-800"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800 dark:text-green-300">{sig.clientName}</span>
                  </div>
                  <span className="text-xs text-green-600">
                    Signed{' '}
                    {new Date(sig.signedAt ?? '').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              ))}
              {contract.companySignedAt && (
                <div className="flex items-center justify-between px-4 py-3 bg-green-50 border border-green-100 rounded-lg dark:bg-green-950/30 dark:border-green-800">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800 dark:text-green-300">
                      {contract.company.name} (Company)
                    </span>
                  </div>
                  <span className="text-xs text-green-600">
                    Signed{' '}
                    {new Date(contract.companySignedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Already acted state ────────────────────────────── */}
        {!canSign && !actionComplete && contract.status !== 'LOST' && (
          <>
            {contract.status === 'WON' ? (
              <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50/50 dark:border-green-700 dark:from-green-950/30 dark:to-emerald-950/20">
                <CardContent className="pt-6 pb-6">
                  <div className="text-center mb-5">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-3 dark:bg-green-900/30">
                      <CheckCircle2 className="h-7 w-7 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-1">Project Active</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      This contract has been fully signed by all parties. Your project is active and underway.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-lg mx-auto">
                    <div className="flex items-center gap-2 px-4 py-2 bg-card/70 rounded-xl border border-green-100 text-sm text-foreground">
                      <HardHat className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Fulfillment tasks are in progress</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-card/70 rounded-xl border border-green-100 text-sm text-foreground">
                      <Mail className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Check your email for updates</span>
                    </div>
                  </div>

                  {contract.salesRep.name && (
                    <div className="text-center mt-4">
                      <p className="text-xs text-muted-foreground">
                        Questions? Contact <span className="font-medium text-foreground">{contract.salesRep.name}</span>
                        {contract.salesRep.email && <> at <span className="font-medium text-foreground">{contract.salesRep.email}</span></>}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-amber-200 bg-amber-50/30 dark:border-amber-700 dark:bg-amber-950/20">
                <CardContent className="pt-6 text-center">
                  <CheckCircle2 className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-foreground mb-1">
                    Contract Processed
                  </p>
                  <p className="text-sm text-muted-foreground">
                    This contract has already been processed.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {contract.status === 'LOST' && !actionComplete && (
          <Card className="border-red-200 bg-red-50/30 dark:border-red-800 dark:bg-red-950/20">
            <CardContent className="pt-6 text-center">
              <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-foreground">This contract has been lost.</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* ── Decline Dialog ───────────────────────────────────── */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Decline Contract
            </DialogTitle>
            <DialogDescription>
              Please let us know why you are declining this contract.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label htmlFor="decline-reason">Reason (optional)</Label>
              <Textarea
                id="decline-reason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="e.g. Budget constraints, timing issues, terms disagreement..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => setDeclineDialogOpen(false)}
              disabled={declineMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => declineMutation.mutate()}
              disabled={declineMutation.isPending}
              variant="destructive"
            >
              {declineMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Declining...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Decline Contract
                </>
              )}
            </Button>
          </div>
          {declineMutation.isError && (
            <p className="text-sm text-red-600 mt-2">
              {(declineMutation.error as Error)?.message || 'Something went wrong. Please try again.'}
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="border-t border-border bg-card mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 text-center">
          <p className="text-xs text-muted-foreground/70">
            Powered by LarkPM &mdash; {contract.company.name}
          </p>
        </div>
      </footer>
    </div>
  );
}
